import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';

import { CYCLE_HANDLING, HubInfo, HubInfoUrlProvider, Layout, Logger, RenderingContext } from './../../api';
import { isReferenceElement } from './../elements/element';
import { anyToString, arrayPush, assertObject, cloneDeep, KEY_DEBUG } from './../js/js.utils';
import { DEFAULT_LAYOUT_MAPPING, DEFAULT_LAYOUT_MODE } from './../layouts/layout';
import { NOOP_LOGGER } from './../logger/noop.logger';
import { isArray, isFunction, isNil, isNotNil, isString } from './../predicates/predicates';
import { luceneEscapeKeyValue } from './../search/search.utils';
import { DEFAULT_SITE, EMPTY_SITE_CONTEXT } from './../site/sites.constants';
import { parseQueryString, parseURL, queryToString } from './../url/url.utils';

export interface WchDebug {
  path: string;
}

export interface RenderingContextMap {
  [id: string]: RenderingContext;
}

export const KEY_RENDERING_CONTEXT_MAP = Symbol('RenderingContextMap');

export const KEY_CYCLE = '$$CYCLE';
export const KEY_REFERENCE = '$$REF';

export const EMPTY_RENDERING_CONTEXT: RenderingContext = {
  id: null,
  elements: {},
  layouts: DEFAULT_LAYOUT_MAPPING,
  markups: {},
  context: EMPTY_SITE_CONTEXT
};

export const UNDEFINED_RENDERING_CONTEXT: RenderingContext = {
  id: undefined,
  elements: {},
  layouts: DEFAULT_LAYOUT_MAPPING,
  markups: {},
  context: EMPTY_SITE_CONTEXT
};

// regular expression to detect uuid.v4 strings
const HEX_REGEXP_STRING = '[0-9a-fA-F]';
const HOST_REGEXP_STRING = '[^\\.\\:]';
const UUID_V4_REGEXP_STRING =
  `${HEX_REGEXP_STRING}{8}-${HEX_REGEXP_STRING}{4}-4${HEX_REGEXP_STRING}{3}-[89abAB]${HEX_REGEXP_STRING}{3}-${HEX_REGEXP_STRING}{12}`;
const TENANT_BASED_URL = `^(?:\\/api)?\\/(${UUID_V4_REGEXP_STRING})(?:\\/)?(?:.*)$`;
const TENANT_BASED_URL_REGEXP = new RegExp(TENANT_BASED_URL);

// check for preview hosts
const PREVIEW_INDICATOR = '(?:-preview|-preview-stage)';
const PREVIEW_HOST_REGEXP_STRING = `^(?:(${HOST_REGEXP_STRING}+)${PREVIEW_INDICATOR})((?:\\.${HOST_REGEXP_STRING}+)+)$`;
const PREVIEW_HOST_REGEXP = new RegExp(PREVIEW_HOST_REGEXP_STRING);

// check for preview origins
const PREVIEW_ORIGIN_REGEXP_STRING = `^http[s]?:\\/\\/(?:(${HOST_REGEXP_STRING}+)${PREVIEW_INDICATOR})((?:\\.${HOST_REGEXP_STRING}+)+)(:\\d+)?$`;
const PREVIEW_ORIGIN_REGEXP = new RegExp(PREVIEW_ORIGIN_REGEXP_STRING);

const _ALL_FETCH_LEVELS = -1;
const _DEFAULT_FETCH_LEVELS = _ALL_FETCH_LEVELS;

/*
 * Maps unknown or empty modes
 *
 * @param aMode the mode
 * @return the mode if not empty, else DEFAULT_LAYOUT_MODE
 */
function _boxLayoutMode(aMode: string | null | undefined): string {
  return (isNil(aMode) || (aMode.length === 0)) ? DEFAULT_LAYOUT_MODE : aMode;
}

/*
 * Returns the layout object for a particular mode for the rendering context
 *
 * @param aLayoutMode   the mode
 * @param aContext  the context
 */
function _getLayout(aLayoutMode: string | undefined, aContext: RenderingContext | undefined): Layout | undefined {
  // map the layout
  const layoutMode = aLayoutMode || DEFAULT_LAYOUT_MODE;
  // decode the ID
  const contentId = isNotNil(aContext) ? aContext.id : undefined;
  // check
  if (aContext && aContext.layouts) {
    // resolve
    return aContext.layouts[layoutMode] || aContext.layouts[DEFAULT_LAYOUT_MODE];
  }
}

/*
 * Determines if the server is running in preview mode
 *
 * @param aBaseURL the base URL
 */
function _isPreviewMode(aBaseURL: URL): boolean {
  // extract the hostname
  return PREVIEW_HOST_REGEXP.test(aBaseURL.hostname);
}

/*
 * Determines if the server is running in preview mode
 *
 * @param aOrigin the origin
 */
function _isPreviewOrigin(aOrigin: string): boolean {
  // extract the hostname
  return PREVIEW_ORIGIN_REGEXP.test(aOrigin);
}

// undefined hub info
const UNDEFINED_HUB_INFO: HubInfo = {
  apiUrl: undefined,
  deliveryUrl: undefined
};

/* Decodes the hub info from the base URL. This assumes
* that the application is served from a location in WCH.
*/
function _getHubInfoFromBaseURL(aBaseURL: URL): HubInfo {
  // sanity check
  if (isNil(aBaseURL)) {
    // nothing meaningful to return
    return UNDEFINED_HUB_INFO;
  }
  // log the URL
  // li(LOGGER, 'getHubInfoFromBaseURL', aBaseURL, TENANT_BASED_URL_REGEXP);
  // the result
  let apiUrl;
  let deliveryUrl;
  // match
  const origin = aBaseURL.origin;
  const result = TENANT_BASED_URL_REGEXP.exec(aBaseURL.pathname);
  if (result && (result.length > 1)) {
    // extract the tenant
    const tenantID = result[1];
    // build the URLs
    apiUrl = parseURL(`${origin}/api/${tenantID}/`);
    deliveryUrl = parseURL(`${origin}/${tenantID}/`);
  } else {
    // not tenant based
    apiUrl = parseURL(`${origin}/api/`);
    deliveryUrl = parseURL(`${origin}/`);
  }
  // log the URL
  // li(LOGGER, 'apiUrl', apiUrl, 'deliveryUrl', deliveryUrl);
  // construct the info
  return {
    apiUrl: apiUrl,
    deliveryUrl: deliveryUrl
  };
}

/**
* Computes the delivery URL given the API URL
*
* @param aApiURL   the API URL
* @return the delivery URL
*/
function _getDeliveryUrlFromApiURL(aApiURL: URL): URL {
  // just dispatch
  return parseURL(_getHubInfoFromBaseURL(aApiURL).deliveryUrl);
}

/**
* Computes the API URL given the delivery URL
*
* @param aDeliveryURL   the delivery URL
* @return the API URL
*/
function _getApiUrlFromDeliveryURL(aDeliveryURL: URL): URL {
  // just dispatch
  return parseURL(_getHubInfoFromBaseURL(aDeliveryURL).apiUrl);
}

/*
 * Returns the number of fetch levels
 *
 * @param aLevels   the levels
 * @return the number, a value < 0 means to fetch all levels
 */
function _boxFetchLevels(aLevels: number | null | undefined, aDefault?: number): number {
  return isNotNil(aLevels) ? (aLevels < 0) ? _ALL_FETCH_LEVELS : aLevels : aDefault ? aDefault : _DEFAULT_FETCH_LEVELS;
}

/**
 * Converts the provider into a URL
 *
 * @param aProvider     the provider
 * @return the URL if available
 */
function _fromHubInfoUrlProvider(aProvider: HubInfoUrlProvider): URL | null | undefined {
  // sanity checks
  return isNil(aProvider) ? aProvider : isFunction(aProvider) ? _fromHubInfoUrlProvider(aProvider()) : parseURL(aProvider);
}

function _getCycleHandling(aCycle?: CYCLE_HANDLING | string): CYCLE_HANDLING {
  // sanity check
  return isString(aCycle) ? CYCLE_HANDLING[aCycle] : isNotNil(aCycle) ? aCycle : undefined;
}

export const KEY_RENDERING_CONTEXT = 'renderingContext';
export const KEY_LAYOUT_MODE = 'layoutMode';
export const KEY_LEVELS = 'levels';

// some query constants
const _KEY_SEARCH_Q = 'q';
const _VALUE_SEARCH_Q = '*:*';
const _KEY_FIELD_LIST = 'fl';
const _KEY_SORT = 'sort';
const _KEY_ROWS = 'rows';
const _KEY_PATHS = 'paths';
const _VALUE_ONE_ROW = '1';
const _VALUE_MAX_ROWS = anyToString(0x7FFFFFFF);
const _VALUE_FIELD_LIST = 'document:[json]';
const _KEY_SEARCH_FQ = 'fq';
const _VALUE_SEARCH_FQ_CONTENT = 'classification:(content)';
const _VALUE_SEARCH_FQ_PAGE = 'classification:(page)';
const _PREFIX_SEARCH_CLASSIFICATION = 'classification';
const _FQ_SITE_ID = 'siteId';

/*
 * Tests if a field list represents a classification
 *
 * @param aValue   the value
 * @return true for a classification, else false
 */
function _isSearchClassification(aValue: string): boolean {
    return isNotNil(aValue) && (aValue.indexOf(_PREFIX_SEARCH_CLASSIFICATION) === 0);
}

/*
 * Adds an FQ value to an array
 *
 * @param aValue the value to add
 * @param aArray the array to add the value to
*/
function _fqValuePush(aValue: string, aArray: string[]): string[] {
    // push if not a search classification
    return !_isSearchClassification(aValue) ? arrayPush(aValue, aArray) : aArray;
}

/**
 * Generically adjust the search query
 *
 * @param aQuery        the query
 * @param aFieldQuery   the field
 *
 * @return  the new query string
 */
function _adjustSearchQuery(aQuery: string, aFieldQueries: string[]): string {
    // sanity check
    if (isNotNil(aQuery)) {
        // parse the query
        const params = parseQueryString(aQuery);
        // check if we have the q value
        if (!params.hasOwnProperty(_KEY_SEARCH_Q)) {
            // inject the default value
            params[_KEY_SEARCH_Q] = _VALUE_SEARCH_Q;
        }
        /*
         * replace the field list to make sure
         * we have the full document included in the result
         */
        params[_KEY_FIELD_LIST] = _VALUE_FIELD_LIST;

        /*
         * Check if the parameters have the fq values
         */
        const fqs = params[_KEY_SEARCH_FQ];
        const newFqs = cloneDeep(aFieldQueries);
        // append
        if (isString(fqs)) {
            _fqValuePush(fqs, newFqs);
        } else
            if (isArray(fqs)) {
                // current value
                const lenFqs = fqs.length;
                for (let i = 0; i < lenFqs; ++i) {
                    _fqValuePush(fqs[i], newFqs);
                }
            }
        // set the value
        params[_KEY_SEARCH_FQ] = newFqs;
        // convert back to a string
        return queryToString(params);
    }
    // return the query as is
    return aQuery;
}

/*
 * Analyzes the search query and makes sure that it contains the required fields.
 *
 * @param aQuery the search query
 * @return the modified query
 */
function _adjustRenderingContextSearchQuery(aQuery: string): string {
    // dispatch
    return _adjustSearchQuery(aQuery, [_VALUE_SEARCH_FQ_CONTENT]);
}

/*
 * Analyzes the search query and makes sure that it contains the required fields.
 *
 * @param aQuery the search query
 * @return the modified query
 */
function _adjustSitePagesSearchQuery(aQuery: string, aSiteId: string): string {
    // dispatch
    return _adjustSearchQuery(aQuery, [_VALUE_SEARCH_FQ_PAGE, luceneEscapeKeyValue(_FQ_SITE_ID, aSiteId)]);
}

/**
 * Returns the search expression that finds the siblings of a page
 *
 * @param aParentId id of the parent page
 * @return the string
 */
function _getSiblingsExpression(aParentId: string): string {
    // construct the expression
    return `(${luceneEscapeKeyValue('parentId', aParentId)} AND hideFromNavigation:("false"))`;
}

/**
 * Returns the search expression that finds the children of a page
 *
 * @param aPageId Id of the page
 * @return the string
 */
function _getChildrenExpression(aPageId: string): string {
    // construct the expression
    return _getSiblingsExpression(aPageId);
}

/**
 * Returns the parent of a page
 *
 * @param aPageId Id of the page
 * @return the string
 */
function _getParentExpression(aParentId: string): string {
    return `(${luceneEscapeKeyValue('id', aParentId)})`;
}

/**
 * Returns the expression to get the complete site context in one call
 *
 * @param aParentId     the parent ID
 * @param aPageId       the page ID
 * @return the expression
 */
function _getSiteContextExpression(aParentId: string | null | undefined, aPageId: string): string {
    return `(${_getParentExpression(aParentId)} OR ${_getChildrenExpression(aPageId)} OR ${_getSiblingsExpression(aParentId)})`;
}

/**
 * Builds the search query string
 *
 * @param aQueryString  the query string
 * @return the result
 */
function _getSearchURL(aQueryString: string): Observable<string> {
    // dispatch
    return of(`delivery/v1/search?${aQueryString}`);
}

/*
 * Returns the URI for a page search URL
 *
 * @param aID   the ID
 * @return the path
 */
function _getPageSearchURL(aPath: string, aSiteId: string): Observable<string> {
    // the query parameters
    const params = {};
    // construct the search string
    params[_KEY_SEARCH_Q] = luceneEscapeKeyValue(_KEY_PATHS, aPath);
    params[_KEY_SEARCH_FQ] = [_VALUE_SEARCH_FQ_PAGE, luceneEscapeKeyValue(_FQ_SITE_ID, aSiteId)];
    params[_KEY_FIELD_LIST] = _VALUE_FIELD_LIST;
    params[_KEY_ROWS] = _VALUE_ONE_ROW;
    // build the final string
    return _getSearchURL(queryToString(params));
}

/*
 * Returns the URI for a page search URL
 *
 * @param aID   the ID
 * @return the path
 */
function _getSiteContextURL(aParentId: string | null | undefined, aPageId: string, aSiteId: string): Observable<string> {
    // the query parameters
    const params = {};
    // construct the search string
    params[_KEY_SEARCH_Q] = _getSiteContextExpression(aParentId, aPageId);
    params[_KEY_SEARCH_FQ] = [_VALUE_SEARCH_FQ_PAGE, luceneEscapeKeyValue(_FQ_SITE_ID, aSiteId)];
    params[_KEY_FIELD_LIST] = _VALUE_FIELD_LIST;
    params[_KEY_ROWS] = _VALUE_MAX_ROWS;
    params[_KEY_SORT] = 'position asc, lastModified desc, id asc';
    // build the final string
    return _getSearchURL(queryToString(params));
}

/*
 * Returns the URI for a parent page search
 *
 * @param aID   the ID
 * @return the path
 */
function _getParentPageURL(aParentId: string, aSiteId: string): Observable<string> {
    // the query parameters
    const params = {};
    // construct the search string
    params[_KEY_SEARCH_Q] = _getParentExpression(aParentId);
    params[_KEY_SEARCH_FQ] = [_VALUE_SEARCH_FQ_PAGE, luceneEscapeKeyValue(_FQ_SITE_ID, aSiteId)];
    params[_KEY_FIELD_LIST] = _VALUE_FIELD_LIST;
    params[_KEY_ROWS] = _VALUE_ONE_ROW;
    // build the final string
    return _getSearchURL(queryToString(params));
}

function _addDebug(aPath: string, aRenderingContext: RenderingContext): RenderingContext {
    // adds debug info
    const debug: WchDebug = assertObject<WchDebug>(KEY_DEBUG, aRenderingContext);
    debug.path = aPath;
    // ok
    return aRenderingContext;
}

/* Resolves the rendering context according to the configured strategy */
function _resolveRenderingContext(aRenderingContext: RenderingContext | null | undefined,
    aStrategy: CYCLE_HANDLING,
    aRenderingContextById: (id: string) => Observable<RenderingContext | null | undefined>,
    aLogger?: Logger):
    Observable<RenderingContext | null | undefined> {
     // logger
    const logger = aLogger || NOOP_LOGGER;
    // sanity check
    if (isNotNil(aRenderingContext)) {
        // access the context
        const allContexts: RenderingContextMap = aRenderingContext[KEY_RENDERING_CONTEXT_MAP];
        if (isNotNil(allContexts)) {
            // test if this is a reference
            const ref = aRenderingContext[KEY_REFERENCE];
            if (isNotNil(ref)) {
                // check if we need to load this beast
                logger.info('lazyLoad', '[', ref, ']');
                // lazy load the context
                return aRenderingContextById(ref);
            }
            // resolve
            const id = aRenderingContext.id;
            // check if we have a resolved context
            const resolvedContext = allContexts[id];
            if (isNotNil(resolvedContext)) {
                // check if this represents a cyclic reference
                if (resolvedContext[KEY_CYCLE]) {
                    // check if we need to load this beast
                    logger.info('lazyLoad', '[', id, ']');
                    // lazy load the context
                    return aRenderingContextById(id);
                }
                // the strategy
                logger.info('cycleHandlingStrategy', aStrategy);
                // replace cyclic reference
                if ((resolvedContext !== aRenderingContext) && (aStrategy === CYCLE_HANDLING.RESOLVE)) {
                    // replace the reference
                    logger.info('replacing cyclic reference', '[', id, ']');
                    // return this context
                    return of(resolvedContext);
                }
            }
        }
    }
    // fallback to the original context
    return of(aRenderingContext);
}

const VALUE_ROOT = '$';

export declare type RenderingContextCallback = (rc: RenderingContext, parent?: RenderingContext, path?: string) => void;

/*
 * Iterates over all rendering contexts referenced by this context
 *
 * @param aRenderingContext     the rendering context
 * @param aCallback     the callback
 */
function _internalForEachRenderingContexts(
    aRenderingContext: RenderingContext[],
    aParent: RenderingContext | undefined,
    aPath: string,
    aCallback: RenderingContextCallback) {
    // dispatch
    let lenContext = aRenderingContext.length;
    while (lenContext-- > 0) {
        // new path
        const path = `${aPath}[${lenContext}]`;
        // dispatch
        _internalForEachRenderingContext(aRenderingContext[lenContext], aParent, path, aCallback);
    }
}

/*
 * Iterates over all rendering contexts referenced by this context
 *
 * @param aRenderingContext     the rendering context
 * @param aCallback     the callback
 */
function _internalForEachRenderingContext(
    aRenderingContext: RenderingContext,
    aParent: RenderingContext | undefined,
    aPath: string,
    aCallback: RenderingContextCallback) {
    // recurse into reference elements
    const elements = aRenderingContext.elements;
    if (isNotNil(elements)) {
        // analyze the elements
        // tslint:disable-next-line:forin
        for (const elementName in elements) {
            // current element
            const element = elements[elementName];
            // check for the type
            if (isReferenceElement(element)) {
                // new path
                const path = `${aPath}.elements.${elementName}`;
                // test for the values
                const e: any = element;
                const v: any = e.value || e.values;
                // check for
                if (isArray(v)) {
                    // recurse across the values
                    _internalForEachRenderingContexts(v as RenderingContext[], aRenderingContext, path, aCallback);
                } else
                    if (isNotNil(v)) {
                        // assume a rendering context
                        _internalForEachRenderingContext(v as RenderingContext, aRenderingContext, path, aCallback);
                    }


            }
        }
    }
    // notify about this context
    aCallback(aRenderingContext, aParent, aPath);
}

/*
 * Iterates over all rendering contexts referenced by this context
 *
 * @param aRenderingContext     the rendering context
 * @param aCallback     the callback
 */
function _forEachRenderingContexts(
    aRenderingContext: RenderingContext[],
    aCallback: RenderingContextCallback) {
    // dispatch
    return _internalForEachRenderingContexts(aRenderingContext, undefined, VALUE_ROOT, aCallback);
}

/*
 * Iterates over all rendering contexts referenced by this context
 *
 * @param aRenderingContext     the rendering context
 * @param aCallback     the callback
 */
function _forEachRenderingContext(
    aRenderingContext: RenderingContext,
    aCallback: RenderingContextCallback) {
    // root name
    const ROOT_PATH = isNotNil(aRenderingContext) ? aRenderingContext.id : VALUE_ROOT;
    // dispatch
    return _internalForEachRenderingContext(aRenderingContext, undefined, ROOT_PATH, aCallback);
}

/*
 * Returns the URI path for the rendering context
 *
 * @param aID   the ID
 * @return the path
 */
function _getRenderingContextURL(aBaseUrl: string, aID: string): string {
    return `${aBaseUrl}context/${encodeURIComponent(aID)}`;
}

/*
* Returns the URI path for the site
*
* @return the path
*/
function _getSiteURL(aBaseUrl: string, aSiteID?: string): string {
  // replace
  return `${aBaseUrl}sites/${encodeURIComponent(aSiteID || DEFAULT_SITE)}`;
}

export {
  _getHubInfoFromBaseURL as wchGetHubInfoFromBaseURL,
  _getDeliveryUrlFromApiURL as wchGetDeliveryUrlFromApiURL,
  _getApiUrlFromDeliveryURL as wchGetApiUrlFromDeliveryURL,
  _fromHubInfoUrlProvider as wchUrlfromHubInfoUrlProvider,
  _getLayout as wchGetLayout,
  _isPreviewMode as wchIsPreviewMode,
  _isPreviewOrigin as wchIsPreviewOrigin,
  _boxLayoutMode as wchBoxLayoutMode,
  _boxFetchLevels as wchBoxFetchLevels,
  _getCycleHandling as wchCycleHandling,
  _DEFAULT_FETCH_LEVELS as DEFAULT_FETCH_LEVELS,
  _addDebug as wchAddDebug,
  _adjustRenderingContextSearchQuery as wchAdjustRenderingContextSearchQuery,
  _adjustSitePagesSearchQuery as wchAdjustSitePagesSearchQuery,
  _resolveRenderingContext as wchResolveRenderingContext,
  _getSearchURL as wchGetSearchURL,
  _getPageSearchURL as wchGetPageSearchURL,
  _getSiteContextURL as wchGetSiteContextURL,
  _getParentPageURL as wchGetParentPageURL,
  _forEachRenderingContext as wchForEachRenderingContext,
  _forEachRenderingContexts as wchForEachRenderingContexts,
  _getSiteURL as wchGetSiteURL,
  _getRenderingContextURL as wchGetRenderingContextURL
};
